import { QueryClient } from "@tanstack/react-query"

import { apiFetch } from "./api"
import { queryKeys } from "./queryKeys"
import type {
  AdminProduct,
  AdminProductDetail,
  Category,
  Customer,
  DeliveryStatus,
  Invoice,
  KitchenDayDelivery,
  Lot,
  Order,
  OrderStatus,
  PackingStop,
  PrepItem,
  ProductVariant,
  StockBalance,
  WorkOrder,
} from "./types"

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, staleTime: 10_000 },
  },
})

export function kitchenOpsInvalidation(date: string) {
  return [
    queryKeys.kitchenDay(date),
    queryKeys.kitchenPrep(date),
    queryKeys.kitchenPacking(date),
    queryKeys.stock,
    queryKeys.orders({}),
  ]
}

export const api = {
  categories: () => apiFetch<{ categories: Category[] }>("/v1/admin/categories"),
  customer: (id: string) => apiFetch<{ customer: Customer }>(`/v1/admin/customers/${id}`),
  customers: () => apiFetch<{ customers: Customer[] }>("/v1/admin/customers"),
  invoice: (id: string) => apiFetch<{ invoice: Invoice }>(`/v1/admin/orders/${id}/invoice`),
  kitchenDay: (date: string) =>
    apiFetch<{ date: string; deliveries: KitchenDayDelivery[] }>(
      `/v1/admin/kitchen/day?date=${date}`,
    ),
  kitchenPacking: (date: string) =>
    apiFetch<{ date: string; windows: Array<{ window: string; stops: PackingStop[] }> }>(
      `/v1/admin/kitchen/packing?date=${date}`,
    ),
  kitchenPrep: (date: string) =>
    apiFetch<{ date: string; items: PrepItem[] }>(`/v1/admin/kitchen/prep?date=${date}`),
  lots: () => apiFetch<{ lots: Lot[] }>("/v1/admin/lots"),
  order: (id: string) => apiFetch<{ order: Order }>(`/v1/admin/orders/${id}`),
  orders: (filters: { status?: string; date?: string; customerId?: string } = {}) => {
    const params = new URLSearchParams()
    if (filters.status) params.set("status", filters.status)
    if (filters.date) params.set("date", filters.date)
    if (filters.customerId) params.set("customerId", filters.customerId)
    const q = params.toString()
    return apiFetch<{ orders: Order[] }>(`/v1/admin/orders${q ? `?${q}` : ""}`)
  },
  product: (id: string) => apiFetch<AdminProductDetail>(`/v1/admin/products/${id}`),
  products: (filters: { kind?: string; q?: string; active?: string } = {}) => {
    const params = new URLSearchParams()
    if (filters.kind) params.set("kind", filters.kind)
    if (filters.q) params.set("q", filters.q)
    if (filters.active) params.set("active", filters.active)
    const q = params.toString()
    return apiFetch<{ products: AdminProduct[] }>(`/v1/admin/products${q ? `?${q}` : ""}`)
  },
  stock: () => apiFetch<{ balances: StockBalance[] }>("/v1/admin/stock"),
  workOrder: (id: string, date: string) =>
    apiFetch<{ workOrder: WorkOrder }>(`/v1/admin/orders/${id}/work-order/${date}`),

  adjustStock: (body: { productId: number; quantity: number; reason?: string; lotId?: number }) =>
    apiFetch<{ balances: StockBalance[] }>("/v1/admin/stock/adjustments", {
      body: JSON.stringify(body),
      method: "POST",
    }),
  bulkDayStatus: (date: string, status: DeliveryStatus) =>
    apiFetch<{ updated: number }>(`/v1/admin/kitchen/day/${date}/status`, {
      body: JSON.stringify({ status }),
      method: "POST",
    }),
  createLot: (body: { productId: number; lotCode: string; qty: number; expiresOn?: string }) =>
    apiFetch<{ lot: Lot }>("/v1/admin/lots", { body: JSON.stringify(body), method: "POST" }),
  createProduct: (body: Record<string, unknown>) =>
    apiFetch<{ product: unknown }>("/v1/admin/products", {
      body: JSON.stringify(body),
      method: "POST",
    }),
  explodeBom: (body: { productId: number; variantId?: number | null; quantity?: number }) =>
    apiFetch<{
      components: Array<{ id: number; sku: string; name: string; quantity: number; unit: string }>
    }>("/v1/admin/boms/explode", { body: JSON.stringify(body), method: "POST" }),
  patchDeliveryStatus: (orderId: number, date: string, status: DeliveryStatus) =>
    apiFetch<{ order: Order }>(`/v1/admin/orders/${orderId}/deliveries/${date}`, {
      body: JSON.stringify({ status }),
      method: "PATCH",
    }),
  patchOrderStatus: (orderId: number, status: OrderStatus) =>
    apiFetch<{ order: Order }>(`/v1/admin/orders/${orderId}`, {
      body: JSON.stringify({ status }),
      method: "PATCH",
    }),
  putBom: (body: {
    productId: number
    variantId?: number | null
    lines: Array<{ componentProductId: number; quantity: number; unit: string }>
  }) => apiFetch<{ bom: unknown }>("/v1/admin/boms", { body: JSON.stringify(body), method: "PUT" }),
  createVariant: (
    productId: number,
    body: {
      sku: string
      name: string
      price?: number
      soldOut?: boolean
      isDefault?: boolean
      optionPairs?: Array<{ attribute: string; name: string }>
      copyBomFromVariantId?: number | null
    },
  ) =>
    apiFetch<{ variant: ProductVariant }>(`/v1/admin/products/${productId}/variants`, {
      body: JSON.stringify(body),
      method: "POST",
    }),
  updateVariant: (
    productId: number,
    variantId: number,
    body: {
      sku?: string
      name?: string
      price?: number
      soldOut?: boolean
      isDefault?: boolean
      optionPairs?: Array<{ attribute: string; name: string }>
    },
  ) =>
    apiFetch<{ variant: ProductVariant }>(`/v1/admin/products/${productId}/variants/${variantId}`, {
      body: JSON.stringify(body),
      method: "PATCH",
    }),
  deleteVariant: (productId: number, variantId: number) =>
    apiFetch<{ deleted: boolean; id: number }>(
      `/v1/admin/products/${productId}/variants/${variantId}`,
      { method: "DELETE" },
    ),
  updateProduct: (id: number, body: Record<string, unknown>) =>
    apiFetch<{ product: unknown }>(`/v1/admin/products/${id}`, {
      body: JSON.stringify(body),
      method: "PATCH",
    }),
  uploadProductImage: (productId: number, appearance: "light" | "dark", file: File) => {
    const body = new FormData()
    body.set("appearance", appearance)
    body.set("file", file)
    return apiFetch<AdminProductDetail>(`/v1/admin/products/${productId}/images`, {
      body,
      method: "POST",
    })
  },
}
